import React, {useEffect, useState} from "react";
import {Button, Descriptions, Form, Input, Select, Spin} from "antd";
import {message} from "../../utils/antdFeedback";
import {ArrowLeftOutlined} from "@ant-design/icons";
import {LifeBuoy} from "lucide-react";
import {useDispatch, useSelector} from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import {AdminLayout} from "../../components/layout/AdminLayout";
import {AppDispatch, RootState} from "../../redux/store";
import {getSupportTicketById, updateSupportTicketStatus} from "../../redux/slice/supportTicketSlice";
import {HeroHeader, Card, COLORS} from "../../designSystem";

function SupportTicketDetails() {
  const {id} = useParams();
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const {token} = useSelector((state: RootState) => state.account);
  const ticket = useSelector((state: RootState) => state.supportTickets.selectedTicket);
  const [saving, setSaving] = useState(false);
  const isCurrentTicket = ticket && String(ticket.id) === String(id);

  useEffect(() => {
    if (token && id) {
      dispatch(getSupportTicketById({token, ticketId: id}));
    }
  }, [token, id, dispatch]);

  const submit = async (values: any) => {
    if (!token || !id) return;
    setSaving(true);
    try {
      await dispatch(updateSupportTicketStatus({token, ticketId: id, status: values.status, adminNote: values.adminNote})).unwrap();
      message.success("Ticket updated");
      navigate("/support-tickets");
    } catch (error: any) {
      message.error(error || "Could not update ticket");
    } finally {
      setSaving(false);
    }
  };

  if (!isCurrentTicket) {
    return <AdminLayout><div className="m-10"><Spin tip="Loading ticket..."/></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <main className="h-full overflow-y-auto px-6 py-8 md:px-8" style={{background: COLORS.surface}}>
        <div className="mx-auto max-w-4xl">
        <Button className="mb-4" icon={<ArrowLeftOutlined/>} onClick={() => navigate(-1)}>Back</Button>
        <HeroHeader
          icon={LifeBuoy}
          title={ticket.subject}
          subtitle={`Ticket ${ticket.ticketNumber}`}
        />
        <Card className="mt-8 p-5">
        <Descriptions bordered column={1} className="mb-8">
          <Descriptions.Item label="Ticket Number">{ticket.ticketNumber}</Descriptions.Item>
          <Descriptions.Item label="User">{ticket.userName} ({ticket.userEmail})</Descriptions.Item>
          <Descriptions.Item label="Issue Type">{ticket.issueTypeName}</Descriptions.Item>
          <Descriptions.Item label="Description">{ticket.description}</Descriptions.Item>
          <Descriptions.Item label="Related Reference">{ticket.relatedReference || "None"}</Descriptions.Item>
          <Descriptions.Item label="Contact">{ticket.contactEmail || ticket.contactPhone}</Descriptions.Item>
          <Descriptions.Item label="Current Status">{ticket.statusName}</Descriptions.Item>
          <Descriptions.Item label="Admin Note">{ticket.adminNote || "None"}</Descriptions.Item>
        </Descriptions>
        <Form layout="vertical" initialValues={{status: ticket.status, adminNote: ticket.adminNote}} onFinish={submit}>
          <Form.Item label="Status" name="status" rules={[{required: true}]}>
            <Select>
              <Select.Option value={1}>Open</Select.Option>
              <Select.Option value={2}>In Progress</Select.Option>
              <Select.Option value={3}>Resolved</Select.Option>
              <Select.Option value={4}>Closed</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Admin Note" name="adminNote">
            <Input.TextArea rows={4}/>
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>Save Status</Button>
        </Form>
        </Card>
        </div>
      </main>
    </AdminLayout>
  );
}

export default SupportTicketDetails;
